import { Spacer } from "@hackthenorth/north";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Button, DropdownMenu, DropdownMenuSection, TextInput } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import "twin.macro";
import "styled-components/macro";

import { InfoTag } from "src/shared/components";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { useDeviceSize } from "src/shared/hooks";
import { CheckIcon, FilterIcon } from "src/static/icons";
import { useGetEventScheduleQuery } from "src/views/schedule/ScheduleContext/graphql/getEventSchedule.generated";

import Calendar from "./Calendar";
import {
  CUSTOM_EVENT_TYPES,
  SCHEDULE_START,
  SCHEDULE_END_BUFFER,
  SATURDAY_OFFSET,
  SUNDAY_OFFSET,
} from "./scheduleUtils";

const EVENT_TYPE_COLORS: Record<string, string> = {
  "Main Event": "#2395C6",
  Food: "#0F766E",
  Workshop: "#54C152",
  Activity: "#D97706",
  Meetup: "#CB2E2E",
  Sponsor: "#B348C8",
  "Tech Talk": "#4B5563",
  Panel: "#C4A484",
  Judging: "#C4A484",
  "Sponsored Workshop": "#C4A484",
};
const EVENT_TYPES = [...CUSTOM_EVENT_TYPES, ...Object.keys(EVENT_TYPE_COLORS)];

const Schedule: React.FC = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");

  const [day, setDay] = useState<string>("friday");
  const [query, setQuery] = useState<string>("");
  const [events, setEvents] = useState<Set<string>>(new Set(EVENT_TYPES));

  const { loading: fetchingEvents, data } = useGetEventScheduleQuery({
    variables: { eventSlug: CURRENT_EVENT_SLUG },
  });
  const eventsData = useMemo(
    () =>
      (data?.eventSchedule ?? []).filter(
        (event) =>
          !event.tags.includes("archived") &&
          new Date(event.start_time) >= new Date(SCHEDULE_START) &&
          new Date(event.start_time) <= new Date(SCHEDULE_END_BUFFER)
      ),
    [data]
  );
  const availableTypes = EVENT_TYPES.filter((type) =>
    eventsData.some((event) => event.tags.includes(type))
  );
  const allSelected = availableTypes.every((type) => events.has(type));

  const handleFilterEvent = (event: string) => {
    events.has(event)
      ? setEvents(new Set([...Array.from(events)].filter((s) => s !== event)))
      : setEvents(new Set([...Array.from(events), event]));
  };

  const handleSelectAll = () => {
    setEvents(allSelected ? new Set<string>() : new Set(availableTypes));
  };

  const items: DropdownMenuSection[] = [
    [
      ...availableTypes.map((type) => ({
        content: (
          <EventRow>
            <Dot color={EVENT_TYPE_COLORS[type] ?? "#2395C6"} />
            <DropdownText>
              {type === "ceremonies"
                ? "Ceremonies"
                : type === "registration"
                ? "Registration"
                : type}
            </DropdownText>
            {events.has(type) && <StyledCheckIcon />}
          </EventRow>
        ),
        selected: events.has(type),
        dismissOnClick: false,
        action: () => handleFilterEvent(type),
      })),
      {
        content: (
          <Button onClick={() => handleSelectAll()}>
            <ButtonText>
              {allSelected ? "Deselect all" : "Select all"}
            </ButtonText>
          </Button>
        ),
        dismissOnClick: false,
        action: () => {},
      },
    ],
  ];

  const handleClick = (scrollLeft: number) => {
    if (document.getElementById("schedule-container")) {
      document.getElementById("schedule-container")!.scrollLeft = scrollLeft;
    }
  };

  const handleScroll = () => {
    if (document.getElementById("schedule-container")) {
      const scrollLeft =
        document.getElementById("schedule-container")!.scrollLeft;
      if (scrollLeft < SATURDAY_OFFSET) {
        setDay("friday");
      } else if (scrollLeft < SUNDAY_OFFSET) {
        setDay("saturday");
      } else {
        setDay("sunday");
      }
    }
  };

  return (
    <>
      <Controls>
        <div className="text-text-tertiary mb-4">
          Hacking begins at 12AM on Saturday. Starting your hack before this
          time is not permitted as outlined in Hack the North&apos;s Code of
          Conduct.
        </div>
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <OuterContainer isTabletOrSmaller={isTabletOrSmaller}>
          <FlexContainer>
            <SelectDay
              id="fri-button"
              selected={day === "friday"}
              onClick={() => {
                setDay("friday");
                handleClick(0);
              }}
            >
              <ButtonText>Friday</ButtonText>
            </SelectDay>
            <SelectDay
              id="sat-button"
              selected={day === "saturday"}
              onClick={() => {
                setDay("saturday");
                handleClick(SATURDAY_OFFSET);
              }}
            >
              <ButtonText>Saturday</ButtonText>
            </SelectDay>
            <SelectDay
              id="sun-button"
              selected={day === "sunday"}
              onClick={() => {
                setDay("sunday");
                handleClick(SUNDAY_OFFSET);
              }}
            >
              <ButtonText>Sunday</ButtonText>
            </SelectDay>
          </FlexContainer>
          {isTabletOrSmaller && <Spacer height={16} />}
          <SearchAndFilterContainer>
            <TextInput
              type="text"
              placeholder="Search for events"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leadingIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            />
            {availableTypes.length > 0 && (
              <StyledDropdown items={items}>
                <Button>
                  <FlexContainer>
                    <FilterIcon />
                    <Spacer width={8} />
                    <ButtonText>Filter</ButtonText>
                  </FlexContainer>
                </Button>
              </StyledDropdown>
            )}
          </SearchAndFilterContainer>
        </OuterContainer>
      </Controls>
      <Spacer height={24} />
      <Calendar
        eventsData={eventsData}
        fetchingEvents={fetchingEvents}
        handleScroll={handleScroll}
        shownTypes={events}
        queryFilter={query}
      />
    </>
  );
};

const Controls = styled.div`
  padding: 0 40px;
`;

const OuterContainer = styled.div<{ isTabletOrSmaller: boolean }>`
  display: flex;
  flex-direction: ${({ isTabletOrSmaller }) =>
    isTabletOrSmaller ? "column" : "row"};
  justify-content: space-between;
  align-items: start;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchAndFilterContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: end;
  gap: 8px;

  @media (max-width: 360px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SelectDay = styled(InfoTag)<{
  selected: boolean;
}>`
  padding: 4px 10px;
  background-color: ${({ theme, selected }) =>
    selected
      ? theme.globalConstants.color.backgroundPrimary
      : theme.globalConstants.color.backgroundGrayDark};
  color: ${({ theme, selected }) =>
    selected
      ? theme.globalConstants.color.textLight
      : theme.globalConstants.color.textSecondary} !important;
  cursor: pointer;
`;

const ButtonText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
`;

const StyledDropdown = styled(DropdownMenu)`
  z-index: 1;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const DropdownText = styled.p`
  margin-top: 0;
  margin-left: 8px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 400;
  font-size: 16px;
`;

const StyledCheckIcon = styled(CheckIcon)`
  width: 16px;
  margin-left: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
`;

export default Schedule;
